import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Helper to fetch interests for a given user id and merge into user state
  const fetchAndAttachInterests = async (uid) => {
    if (!uid) return null;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/common/get-user-interests/${uid}`,
        { withCredentials: true }
      );
      const interests = Array.isArray(res.data) ? res.data : [];
      setUser((prev) => ({ ...(prev || {}), userInterests: interests }));
      return interests;
    } catch (_) {
      setUser((prev) => ({ ...(prev || {}), userInterests: [] }));
      return [];
    }
  };

  // Load session from server using httpOnly cookie and attach interests
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/auth/session`,
          { withCredentials: true }
        );
        if (isMounted && res.data?.success) {
          const sessionUser = res.data.user;
          setUser(sessionUser);
          // interests are not in JWT, fetch separately
          await fetchAndAttachInterests(sessionUser?.uid);
        }
      } catch (_) {
        // not logged in or token invalid
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email, password, isAdmin = false) => {
    const endpoint = `${process.env.REACT_APP_BASE_URL}/api/auth/user-dashboard`;
    const res = await axios.post(
      endpoint,
      { email, password, isAdmin },
      { withCredentials: true }
    );
    if (res.data?.success) {
      // res.data includes role/access info; cookie is set by server
      setUser(res.data);
      // Ensure userInterests present; if missing (e.g., admins), attach empty array
      if (!Array.isArray(res.data.userInterests)) {
        setUser((prev) => ({ ...(prev || {}), userInterests: [] }));
      }
      return res.data;
    }
    throw new Error(res.data?.message || "Login failed");
  };

  const logout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (_) {
      // ignore
    } finally {
      setUser(null);
    }
  };

  // Public method to refresh interests from server (e.g., after user updates)
  const refreshUserInterests = async () => {
    const uid = user?.uid;
    if (!uid) return [];
    return await fetchAndAttachInterests(uid);
  };

  const value = useMemo(
    () => ({ user, setUser, loading, login, logout, refreshUserInterests }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);


